import { useState, useCallback } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, MessageCircle } from 'lucide-react';
import { useToast } from '@/hooks/useToast';

interface ReplyDialogProps {
  event: NostrEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReplyDialog({ event, open, onOpenChange }: ReplyDialogProps) {
  const [content, setContent] = useState('');
  const { user } = useCurrentUser();
  const { mutate: publish, isPending } = useNostrPublish();
  const { toast } = useToast();

  const handleSubmit = useCallback(() => {
    if (!event || !content.trim()) return;

    // Build tags per NIP-10: e tag with relay hint, p tag for author
    const tags: string[][] = [
      ['e', event.id, '', 'reply'], // NIP-10 marker: 'reply' for direct replies
      ['p', event.pubkey],           // Tag the original author
      ['t', 'bitcoin'],              // Keep it in the bitcoin conversation
    ];

    // Inherit relevant p tags from the parent (for thread awareness)
    for (const tag of event.tags) {
      if (tag[0] === 'p' && tag[1] !== event.pubkey) {
        tags.push(['p', tag[1]]);
      }
    }

    publish(
      {
        kind: 1,
        content: content.trim(),
        tags,
      },
      {
        onSuccess: () => {
          toast({ description: 'Reply published!' });
          setContent('');
          onOpenChange(false);
        },
        onError: () => {
          toast({ description: 'Failed to publish reply. Please try again.' });
        },
      },
    );
  }, [event, content, publish, onOpenChange, toast]);

  const handleClose = useCallback(() => {
    setContent('');
    onOpenChange(false);
  }, [onOpenChange]);

  if (!event) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-bitcoin" />
            Post your reply
          </DialogTitle>
          <DialogDescription>
            Your reply will be published to Nostr relays as a public note.
          </DialogDescription>
        </DialogHeader>

        {!user ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            You need to be logged in to reply. Click the <strong>Join</strong> button in the header.
          </div>
        ) : (
          <>
            <Textarea
              placeholder="Write your reply…"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="min-h-24 resize-none"
              disabled={isPending}
              autoFocus
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span> Replies are public on Nostr</span>
              <span>{content.length > 0 ? `${content.length} chars` : ''}</span>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          {user && (
            <Button
              onClick={handleSubmit}
              disabled={!content.trim() || isPending}
              className="bg-bitcoin hover:bg-bitcoin/90 text-white"
            >
              {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Reply
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
